import React, { useEffect, useState, useRef } from "react";
import KidService from "../../../services/kids";
import UserService from "../../../services/user";
import { useNavigate, useLocation } from "react-router-dom";
import decodeToken from "../../../utils/decodeToken";
import {
  writeSponsorResume,
  readSponsorResume,
  clearSponsorResume,
  sanitizeReturnPath,
} from "../../../utils/sponsorResume";
import onRazorpay from "../../../pages/checkout/RazorPay";
import SubscriptionService from "../../../services/subscription";
import NoImg from "../../../assets/no-image.png";
import moment from "moment";
import "./KidSponsorModal.css";
import { toast } from "react-toastify";
import { VscChromeClose } from "react-icons/vsc";
import { IoChevronDown } from "react-icons/io5"; // Add this import for the dropdown icon
// Import tick icon (using Font Awesome or your own icon library)
// You may need to adjust this import based on your project setup
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faCheck } from "@fortawesome/free-solid-svg-icons";

// Helper function to extract year from any date format
function getYearOfBirth(dateString) {
  if (!dateString) return null;

  // If it's already a 4-digit year
  if (/^\d{4}$/.test(dateString)) {
    return parseInt(dateString);
  }

  // Try parsing as date object
  const date = new Date(dateString);
  if (!isNaN(date.getTime())) {
    return date.getFullYear();
  }

  // Try regex for common formats like DD-MM-YYYY or DD/MM/YYYY
  let matches;

  // Check for format DD-MM-YYYY or YYYY-MM-DD
  matches =
    dateString.match(/(\d{4})-\d{1,2}-\d{1,2}/) ||
    dateString.match(/\d{1,2}-\d{1,2}-(\d{4})/);
  if (matches && matches[1]) {
    return parseInt(matches[1]);
  }

  // Check for format DD/MM/YYYY or YYYY/MM/DD
  matches =
    dateString.match(/(\d{4})\/\d{1,2}\/\d{1,2}/) ||
    dateString.match(/\d{1,2}\/\d{1,2}\/(\d{4})/);
  if (matches && matches[1]) {
    return parseInt(matches[1]);
  }

  // Check for any 4-digit number that could be a year
  matches = dateString.match(/\b(19|20)\d{2}\b/);
  if (matches) {
    return parseInt(matches[0]);
  }

  return null;
}

const KsmToggle = ({ initialState = false, onChange }) => {
  const [isActive, setIsActive] = useState(initialState);

  const handleToggle = () => {
    const newState = !isActive;
    setIsActive(newState);
    if (onChange) {
      onChange(newState);
    }
  };

  const toggleStyle = {
    position: "relative",
    display: "inline-block",
    width: "50px",
    height: "26px",
    backgroundColor: isActive ? "#293E4F" : "#FFFFFF",
    borderRadius: "26px",
    cursor: "pointer",
    transition: "background-color 0.3s ease",
    border: "1px solid #E0E0E0",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
  };

  const circleStyle = {
    position: "absolute",
    top: "2px",
    left: isActive ? "26px" : "2px",
    width: "20px",
    height: "20px",
    backgroundColor: isActive ? "#FFFFFF" : "#293E4F",
    borderRadius: "50%",
    transition: "left 0.3s ease, background-color 0.3s ease",
    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.2)",
  };

  return (
    <div
      style={toggleStyle}
      onClick={handleToggle}
      role="switch"
      aria-checked={isActive}
    >
      <div style={circleStyle}></div>
    </div>
  );
};

const KidSponsorModal = ({ isOpen, onClose, kidId, onBackToProfile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [kid, setKid] = useState({});
  const [months, setMonths] = useState(0);
  const { user_id, full_name, email } = decodeToken();
  const isLoggedIn = Boolean(user_id);
  const [becomeMentor, setBecomeMentor] = useState(false);
  const [isSubscription, setIsSubscription] = useState(true); // Default to Auto-Pay (recommended)
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1); // Track which step we're on: 1=Duration, 2=Payment
  const [dropDown, setDropDown] = useState(false);
  // Add new state variables for form validation
  const [formData, setFormData] = useState({
    name: full_name || "",
    email: email || "",
    phone: "",
  });
  const [formErrors, setFormErrors] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [alreadySponsored, setAlreadySponsored] = useState(false);
  const pendingCheckoutRef = useRef(null);

  const releasePendingCheckout = async () => {
    const pending = pendingCheckoutRef.current;
    if (!pending) return;
    pendingCheckoutRef.current = null;
    try {
      await SubscriptionService.abandonCheckout(pending);
    } catch (err) {
      console.error("Failed to release unpaid checkout:", err);
    }
  };

  // Add validation functions
  const validateName = (name) => {
    if (!name.trim()) {
      return "Name is required";
    }
    if (name.trim().length < 2) {
      return "Name must be at least 2 characters long";
    }
    return "";
  };

  const validateEmail = (email) => {
    if (!email.trim()) {
      return "Email is required";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return "Please enter a valid email address";
    }
    return "";
  };
  const validatePhone = (phone) => {
    if (!phone || !String(phone).trim()) {
      return "Phone number is required";
    }
    return "";
  };

  const getEffectiveContact = () => {
    const n = (formData.name || full_name || "").trim();
    const e = (formData.email || email || "").trim();
    const p = (formData.phone || "").trim();
    return { name: n, email: e, phone: p };
  };

  const validatePaymentDetails = () => {
    const { name: n, email: e, phone: p } = getEffectiveContact();
    const nameError = validateName(n);
    const emailError = validateEmail(e);
    const phoneError = validatePhone(p);
    setFormErrors({ name: nameError, email: emailError, phone: phoneError });
    return !nameError && !emailError && !phoneError;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    setFormErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleDropDown = () => {
    setDropDown(!dropDown);
  };
  useEffect(() => {
    if (isOpen && kidId) {
      setLoading(true);
      setAlreadySponsored(false);
      KidService.fetchKidById(kidId)
        .then(({ data }) => {
          const kidProfile = data.data[0];
          if (kidProfile?.is_sponsored) {
            setKid(kidProfile);
            setAlreadySponsored(true);
            return;
          }
          setKid(kidProfile);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [isOpen, kidId]);

  useEffect(() => {
    if (!isOpen || !kidId) return;
    const refreshKid = () => {
      if (document.visibilityState !== "visible") return;
      KidService.fetchKidById(kidId)
        .then(({ data }) => {
          const kidProfile = data.data[0];
          if (kidProfile?.is_sponsored) {
            setKid(kidProfile);
            setAlreadySponsored(true);
            return;
          }
          setKid(kidProfile);
          setAlreadySponsored(false);
        })
        .catch(() => {});
    };
    document.addEventListener("visibilitychange", refreshKid);
    return () => document.removeEventListener("visibilitychange", refreshKid);
  }, [isOpen, kidId, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(1);
      setBecomeMentor(false);
      setMonths(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isLoggedIn) setBecomeMentor(false);
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isOpen) return;
    const t = decodeToken();
    if (t.user_id) {
      setFormData((prev) => ({
        ...prev,
        name: t.full_name || prev.name,
        email: t.email || prev.email,
      }));
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !kidId || loading) return;
    const resume = readSponsorResume();
    if (!resume || !user_id) return;
    if (String(resume.kidId) !== String(kidId)) return;
    const here = `${location.pathname}${location.search || ""}`;
    if (resume.returnPath !== here) return;
    setMonths(resume.months);
    if (typeof resume.isSubscription === "boolean") {
      setIsSubscription(resume.isSubscription);
    }
    setCurrentStep(2);
    clearSponsorResume();
  }, [isOpen, kidId, loading, user_id, location.pathname, location.search]);

  useEffect(() => {
    if (!isOpen || currentStep !== 2 || !user_id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await UserService.fetchUser({ user_id });
        const row = Array.isArray(data?.data) ? data.data[0] : null;
        if (cancelled || !row) return;
        setFormData((prev) => ({
          ...prev,
          name: row.full_name || prev.name,
          email: row.email || prev.email,
          phone:
            row.mobile_no != null && String(row.mobile_no).trim() !== ""
              ? String(row.mobile_no).trim()
              : prev.phone,
        }));
      } catch {
        /* keep token-based fields */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, currentStep, user_id]);

  // Compute allowed sponsorship durations for this kid
  const defaultDurations = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const configuredDurations =
    Array.isArray(kid.sponsor_allowed_months) &&
    kid.sponsor_allowed_months.length > 0
      ? kid.sponsor_allowed_months
      : null;
  const availableDurations = configuredDurations || defaultDurations;

  const handleMentorToggle = (state) => {
    setBecomeMentor(state);
  };

  // Auto-disable subscription for 1 month (makes no sense for single payment)
  useEffect(() => {
    if (months === 1) {
      setIsSubscription(false);
    } else if (months > 1) {
      // Default to subscription for multi-month
      setIsSubscription(true);
    }
  }, [months]);

  const handleSubscriptionToggle = (state) => {
    setIsSubscription(state);
  };

  // Handle subscription payment using Razorpay Checkout
  const handleSubscriptionPayment = async () => {
    if (kid?.is_sponsored) {
      toast.error("Kid already sponsored.");
      return;
    }

    if (!validatePaymentDetails()) {
      toast.error("Please fill in all required fields correctly");
      return;
    }

    if (months === 0) {
      toast.error("Please select the number of months");
      return;
    }

    const {
      name: payName,
      email: payEmail,
      phone: payPhone,
    } = getEffectiveContact();

    setSubscriptionLoading(true);

    try {
      const subscriptionPayload = {
        kid_id: kidId,
        duration_months: parseInt(months),
        customer_name: payName,
        customer_email: payEmail,
        customer_phone: payPhone,
        mentor_id: user_id ? parseInt(user_id) : null,
        is_anonymous: !user_id,
        category_id: kid.category_id,
        amount: kid.total_expenses,
      };

      // console.log('Creating subscription:', subscriptionPayload);

      const response =
        await SubscriptionService.createSubscription(subscriptionPayload);

      if (response.data.status && response.data.razorpay_subscription) {
        const subscription = response.data.razorpay_subscription;
        const subscriptionId = subscription.id;
        const razorpayCustomerId =
          subscription.customer_id ||
          response.data.subscription?.razorpay_customer_id;
        pendingCheckoutRef.current = {
          subscription_id: response.data.subscription?.id,
          razorpay_subscription_id: subscriptionId,
          kid_id: kidId,
        };

        // Use Razorpay Checkout instead of hosted page
        const options = {
          key: process.env.REACT_APP_RAZOR_KEY_ID,
          subscription_id: subscriptionId,
          ...(razorpayCustomerId ? { customer_id: razorpayCustomerId } : {}),
          name: "Shaagird",
          description: `Sponsor ${kid.name} - Monthly`,
          image: "/logo.png",
          handler: async function (razorpayResponse) {
            // Payment successful
            pendingCheckoutRef.current = null;
            console.log("Payment successful:", razorpayResponse);
            toast.success("Subscription activated successfully!");

            try {
              // Verify and record payment for receipt generation
              // Use subscriptionId from closure, not from razorpayResponse
              const verifyResponse =
                await SubscriptionService.verifyAndRecordPayment({
                  razorpay_payment_id: razorpayResponse.razorpay_payment_id,
                  razorpay_subscription_id: subscriptionId, // Use saved subscription ID
                  razorpay_signature: razorpayResponse.razorpay_signature,
                  kid_id: kidId,
                  mentor_id: user_id ? parseInt(user_id) : null,
                  customer_name: payName,
                  customer_email: payEmail,
                  customer_phone: payPhone,
                  amount: kid.total_expenses,
                  months: months,
                });

              console.log("Payment recorded:", verifyResponse.data);

              // Redirect to success page
              setTimeout(() => {
                window.location.href = `/success/${razorpayResponse.razorpay_payment_id}`;
              }, 1000);
            } catch (verifyError) {
              console.error("Payment verification error:", verifyError);
              // Still redirect to success as payment was completed
              setTimeout(() => {
                window.location.href = `/success/${razorpayResponse.razorpay_payment_id}`;
              }, 1000);
            }
          },
          prefill: {
            name: payName,
            email: payEmail,
            contact: payPhone,
          },
          notes: {
            kid_id: kidId,
            kid_name: kid.name,
          },
          theme: {
            color: "#FDC513",
          },
          modal: {
            ondismiss: async function () {
              toast.info("Payment cancelled");
              await releasePendingCheckout();
              setSubscriptionLoading(false);
            },
          },
        };

        // Load Razorpay checkout
        const razorpay = new window.Razorpay(options);
        razorpay.on("payment.failed", async function (response) {
          console.error("Payment failed:", response.error);
          toast.error(`Payment failed: ${response.error.description}`);
          await releasePendingCheckout();
          setSubscriptionLoading(false);
        });
        razorpay.open();
      } else {
        throw new Error(
          response.data.message || "Failed to create subscription",
        );
      }
    } catch (error) {
      console.error("Subscription creation failed:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to create subscription. Please try again.";
      if (error.response?.status === 409) {
        toast.error("Kid already sponsored.");
      } else {
        toast.error(errorMessage);
      }
      setSubscriptionLoading(false);
    }
  };

  let totalAmt = months * kid.total_expenses;

  let sponsorData = {
    kid_id: kidId,
    kid_date_of_birth: getYearOfBirth(kid.date_of_birth)?.toString() || "",
    kid_name: kid.name,
    kid_bio: kid.bio,
    kid_gender: kid.gender,
    amount: totalAmt,
    mentor_id: user_id ? parseInt(String(user_id), 10) : null,
    for_months: months,
    is_active: true,
    category_name: kid.category_name,
    state: kid.state,
    category_id: kid.category_id,
    mentor_name: full_name,
    email: email,
    is_anonymous: user_id ? false : true,
  };

  // Modify the handlePayment function
  const handlePayment = () => {
    if (kid?.is_sponsored) {
      toast.error("Kid already sponsored.");
      return;
    }

    if (!validatePaymentDetails()) {
      toast.error("Please fill in all required fields correctly");
      return;
    }

    if (months === 0) {
      toast.error("Please select the number of months");
      return;
    }

    // Calculate the total amount
    let amount = months * kid.total_expenses;

    // Ensure amount is valid
    if (amount <= 0) {
      toast.error("Invalid amount. Please try again.");
      return;
    }

    const {
      name: payName,
      email: payEmail,
      phone: payPhone,
    } = getEffectiveContact();

    // Update the sponsorData with the correct amount and validated form data
    sponsorData = {
      ...sponsorData,
      amount: amount,
      mentor_name: payName,
      email: payEmail,
      anonymous_mobile_no: payPhone,
    };

    console.log("Sending payment request with amount:", amount);

    // Call the existing Razorpay integration
    onRazorpay(sponsorData).then((response) => {
      const loggedIn = Boolean(decodeToken().user_id);
      if (response && becomeMentor && !loggedIn) {
        // If payment is successful and becomeMentor is true, navigate to mentor registration
        navigate("/register", {
          state: {
            prefillData: {
              full_name: payName,
              email: payEmail,
              role: "Mentor",
            },
          },
        });
      }
    });
  };

  // Function to save sponsorship data after successful payment
  const saveSponsorship = async (data) => {
    try {
      const response = await KidService.createSponsorship(data);
      if (response.status === 200 || response.status === 201) {
        console.log("Sponsorship created successfully!");
      }
    } catch (error) {
      toast.error("Error creating sponsorship. Please contact support.");
      console.error("Error creating sponsorship:", error);
    }
  };

  // Handler for Next button
  const handleNext = () => {
    if (currentStep !== 1) return;
    if (months === 0) {
      toast.error("Please select the number of months");
      return;
    }
    if (!user_id && becomeMentor) {
      const returnPath = sanitizeReturnPath(
        `${location.pathname}${location.search || ""}`,
      );
      writeSponsorResume({
        kidId,
        months,
        isSubscription,
        returnPath,
      });
      navigate("/login", { state: { from: returnPath } });
      return;
    }
    setCurrentStep(2);
  };

  // Handler for Back button
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  function AgeCalculator({ birthyear }) {
    const currentDate = new Date().getFullYear();
    const yearOfBirth = getYearOfBirth(kid.date_of_birth);

    if (!yearOfBirth) return 0;

    const age = currentDate - yearOfBirth;
    return age;
  }

  function ClassCalculator({ birthyear }) {
    const currentDate = new Date().getFullYear();
    const yearOfBirth = getYearOfBirth(kid.date_of_birth);
    if (!yearOfBirth) return 0;

    const age = currentDate - yearOfBirth;
    if (age <= 5) {
      return 0;
    }

    const studyingClass = age - 5;
    const cappedStudyingClass = Math.min(12, studyingClass);

    const studyCompletion = Math.round((cappedStudyingClass / 12) * 100);

    return studyCompletion;
  }

  // Render content based on current step
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        // Duration Selection step
        return (
          <div className="ksm__sponsorModalStep1Container">
            <p className="ksm__contentRightHeading">Time Period</p>
            <p className="ksm__contentRightText">
              Select the time period for which you want to sponsor
            </p>
            <div
              className="ksm__timePeriodContainer"
              style={{ width: "100%", position: "relative" }}
            >
              <select
                className="form-control mb-3"
                style={{
                  color: "#757575",
                  paddingRight: "40px", // Add padding to make room for the icon
                  appearance: "none", // Remove default arrow
                  WebkitAppearance: "none", // Remove default arrow in Safari
                  MozAppearance: "none", // Remove default arrow in Firefox
                }}
                value={months}
                onChange={(e) => setMonths(parseInt(e.target.value))}
              >
                <option value={0}>Select Time Period</option>
                {availableDurations.map((m) => (
                  <option key={m} value={m}>
                    {m} Month{m > 1 ? "s" : ""}
                  </option>
                ))}
              </select>
              {/* Custom dropdown icon */}
              <div
                style={{
                  position: "absolute",
                  right: "12px",
                  top: window.innerWidth <= 1200 ? "38%" : "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none", // Prevents icon from blocking dropdown clicks
                  color: "#757575",
                }}
              >
                <IoChevronDown size={20} />
              </div>
            </div>

            {/* Payment Method Selection - Smart UI based on duration */}
            {months > 0 && (
              <div style={{ marginBottom: "20px" }}>
                {months === 1 ? (
                  /* Single Month - Just show payment summary */
                  <div
                    style={{
                      background:
                        "linear-gradient(135deg, #FFF8E1 0%, #FFECB3 100%)",
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid #FFD54F",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "8px",
                      }}
                    >
                      <span style={{ fontSize: "14px", color: "#5D4037" }}>
                        🎁 One-Time Gift
                      </span>
                      <span
                        style={{
                          fontSize: "18px",
                          fontWeight: "700",
                          color: "#E65100",
                        }}
                      >
                        ₹{kid.total_expenses?.toLocaleString()}
                      </span>
                    </div>
                    <p
                      style={{
                        fontSize: "12px",
                        color: "#795548",
                        margin: 0,
                        lineHeight: "1.4",
                      }}
                    >
                      💡 Want to make a lasting impact? Select{" "}
                      <strong>2+ months</strong> to become a{" "}
                      <strong>Monthly Guardian</strong> and ensure continuous
                      support for {kid.name?.split(" ")[0] || "this child"}'s
                      education.
                    </p>
                  </div>
                ) : (
                  /* Multiple Months - Show payment options */
                  <div>
                    <p
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#555",
                        marginBottom: "10px",
                      }}
                    >
                      How would you like to support{" "}
                      {kid.name?.split(" ")[0] || "this child"}?
                    </p>

                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        flexDirection: "column",
                      }}
                    >
                      {/* Monthly Guardian - Recommended */}
                      <div
                        onClick={() => setIsSubscription(true)}
                        style={{
                          border: isSubscription
                            ? "2px solid #4CAF50"
                            : "1.5px solid #e0e0e0",
                          borderRadius: "14px",
                          padding: "14px 16px",
                          cursor: "pointer",
                          background: isSubscription
                            ? "linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)"
                            : "#fff",
                          position: "relative",
                          transition: "all 0.2s ease",
                          transform: isSubscription
                            ? "scale(1.01)"
                            : "scale(1)",
                          boxShadow: isSubscription
                            ? "0 4px 20px rgba(76, 175, 80, 0.25)"
                            : "0 2px 8px rgba(0,0,0,0.04)",
                        }}
                      >
                        {/* Most Impact Badge */}
                        <div
                          style={{
                            position: "absolute",
                            top: "-8px",
                            left: "16px",
                            background:
                              "linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)",
                            color: "#fff",
                            padding: "3px 10px",
                            borderRadius: "10px",
                            fontSize: "9px",
                            fontWeight: "700",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            boxShadow: "0 2px 6px rgba(76, 175, 80, 0.3)",
                          }}
                        >
                          💚 Most Impact
                        </div>

                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                          }}
                        >
                          <div
                            style={{
                              width: "22px",
                              height: "22px",
                              borderRadius: "50%",
                              border: isSubscription
                                ? "none"
                                : "2px solid #bdbdbd",
                              background: isSubscription ? "#4CAF50" : "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            {isSubscription && (
                              <span
                                style={{
                                  color: "#fff",
                                  fontSize: "12px",
                                  fontWeight: "bold",
                                }}
                              >
                                ✓
                              </span>
                            )}
                          </div>

                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: "2px",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "15px",
                                  fontWeight: "600",
                                  color: isSubscription ? "#1B5E20" : "#333",
                                }}
                              >
                                🤝 Become a Monthly Guardian
                              </span>
                              <span
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "700",
                                  color: isSubscription ? "#2E7D32" : "#333",
                                }}
                              >
                                ₹{kid.total_expenses?.toLocaleString()}
                                <span
                                  style={{
                                    fontSize: "11px",
                                    fontWeight: "400",
                                  }}
                                >
                                  /mo
                                </span>
                              </span>
                            </div>
                            <div
                              style={{
                                fontSize: "11px",
                                color: isSubscription ? "#388E3C" : "#888",
                                marginTop: "4px",
                                lineHeight: "1.4",
                              }}
                            >
                              Ensure uninterrupted education every month
                            </div>
                            <div
                              style={{
                                fontSize: "10px",
                                color: isSubscription ? "#2E7D32" : "#999",
                                display: "flex",
                                gap: "8px",
                                flexWrap: "wrap",
                                marginTop: "6px",
                              }}
                            >
                              <span
                                style={{
                                  background: isSubscription
                                    ? "rgba(255,255,255,0.7)"
                                    : "#f5f5f5",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                }}
                              >
                                📅 Auto-renews monthly
                              </span>
                              <span
                                style={{
                                  background: isSubscription
                                    ? "rgba(255,255,255,0.7)"
                                    : "#f5f5f5",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                }}
                              >
                                🔓 Cancel anytime
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* One-Time Gift Card */}
                      <div
                        onClick={() => setIsSubscription(false)}
                        style={{
                          border: !isSubscription
                            ? "2px solid #FDC513"
                            : "1.5px solid #e0e0e0",
                          borderRadius: "14px",
                          padding: "14px 16px",
                          cursor: "pointer",
                          background: !isSubscription ? "#FFFDE7" : "#fafafa",
                          transition: "all 0.2s ease",
                          boxShadow: !isSubscription
                            ? "0 4px 15px rgba(253, 197, 19, 0.2)"
                            : "none",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                          }}
                        >
                          <div
                            style={{
                              width: "22px",
                              height: "22px",
                              borderRadius: "50%",
                              border: !isSubscription
                                ? "none"
                                : "2px solid #bdbdbd",
                              background: !isSubscription ? "#FDC513" : "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            {!isSubscription && (
                              <span
                                style={{
                                  color: "#333",
                                  fontSize: "12px",
                                  fontWeight: "bold",
                                }}
                              >
                                ✓
                              </span>
                            )}
                          </div>

                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "14px",
                                  fontWeight: "600",
                                  color: !isSubscription ? "#5D4037" : "#666",
                                }}
                              >
                                🎁 One-Time Gift
                              </span>
                              <span
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "700",
                                  color: !isSubscription ? "#E65100" : "#666",
                                }}
                              >
                                ₹
                                {(
                                  months * (kid.total_expenses || 0)
                                ).toLocaleString()}
                              </span>
                            </div>
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#888",
                                marginTop: "2px",
                              }}
                            >
                              Support {months} month{months > 1 ? "s" : ""} of
                              education in one gift
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!isLoggedIn && (
              <div className="ksm__becomeMentor">
                <div className="ksm__toggleButton">
                  <KsmToggle
                    initialState={becomeMentor}
                    onChange={handleMentorToggle}
                  />
                </div>
                <p className="ksm__mentorText">Become a Mentor</p>
              </div>
            )}
            {!isLoggedIn && becomeMentor && (
              <div className="ksm__mentoringDesc">
                <p className="ksm__mentoringDescTitle">You are sponsoring:</p>
                <p className="ksm__mentoringDescInfo">
                  {kid.name} for {months} months
                </p>
              </div>
            )}
            <div className="ksm__contentRightBottom">
              <button
                className="ksm__NextButton"
                onClick={handleNext}
                disabled={months === 0}
              >
                Next
              </button>
            </div>
          </div>
        );

      case 2:
        // Payment step — guests enter full details; logged-in mentors use account + phone for Razorpay
        return (
          <div className="ksm__sponsorModalStep2Container">
            <div className="ksm__backHeader">
              <img
                src="/modalImages/modalBack.png"
                className="ksm__backButton"
                alt="Back"
                onClick={handleBack}
              />
            </div>
            <p className="ksm__step2Title">
              {isLoggedIn ? "Complete payment" : "Enter Your Information"}
            </p>
            {isLoggedIn ? (
              <div className="ksm__inputForm">
                <p
                  style={{
                    fontSize: "14px",
                    color: "#555",
                    marginBottom: "12px",
                    lineHeight: 1.5,
                  }}
                >
                  Using your account:{" "}
                  <strong>{getEffectiveContact().name || "—"}</strong>
                  <br />
                  {getEffectiveContact().email || "—"}
                </p>
                <div className="ksm__inputFieldContainer">
                  <p className="ksm__inputField">Phone (for payment)</p>
                  <input
                    className={`ksm__inputArea ${
                      formErrors.phone ? "ksm__inputError" : ""
                    }`}
                    type="tel"
                    name="phone"
                    placeholder="Phone number for Razorpay"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                  {formErrors.phone && (
                    <p className="ksm__errorText">{formErrors.phone}</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="ksm__inputForm">
                <div className="ksm__inputFieldContainer">
                  <p className="ksm__inputField">Full Name</p>
                  <input
                    className={`ksm__inputArea ${
                      formErrors.name ? "ksm__inputError" : ""
                    }`}
                    type="text"
                    name="name"
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                  {formErrors.name && (
                    <p className="ksm__errorText">{formErrors.name}</p>
                  )}
                </div>
                <div className="ksm__inputFieldContainer">
                  <p className="ksm__inputField">Email</p>
                  <input
                    className={`ksm__inputArea ${
                      formErrors.email ? "ksm__inputError" : ""
                    }`}
                    type="email"
                    name="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                  {formErrors.email && (
                    <p className="ksm__errorText">{formErrors.email}</p>
                  )}
                </div>
                <div className="ksm__inputFieldContainer">
                  <p className="ksm__inputField">Phone Number</p>
                  <input
                    className={`ksm__inputArea ${
                      formErrors.phone ? "ksm__inputError" : ""
                    }`}
                    type="tel"
                    name="phone"
                    placeholder="Enter your phone number"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                  {formErrors.phone && (
                    <p className="ksm__errorText">{formErrors.phone}</p>
                  )}
                </div>
              </div>
            )}
            {isSubscription ? (
              <button
                className="ksm__paymentButton"
                onClick={handleSubscriptionPayment}
                disabled={subscriptionLoading}
                style={{
                  backgroundColor: subscriptionLoading ? "#ccc" : "#FDC513",
                  color: "#000",
                  fontWeight: "bold",
                  cursor: subscriptionLoading ? "not-allowed" : "pointer",
                }}
              >
                {subscriptionLoading
                  ? "Creating Subscription..."
                  : `Subscribe & Pay ₹${kid.total_expenses?.toLocaleString()}/month`}
              </button>
            ) : (
              <button className="ksm__paymentButton" onClick={handlePayment}>
                Proceed to Pay ₹{totalAmt?.toLocaleString()}
              </button>
            )}
          </div>
        );

      case 3:
        // Confirmation step
        return (
          <div className="ksm__sponsorModalStep3Container">
            <p className="ksm__step3Title">Thank You!</p>
            <p className="ksm__step3Subtitle">
              Your sponsorship has been confirmed
            </p>
            <div className="ksm__confirmationDetails">
              <p>
                You are now sponsoring {kid.name} for {months} months.
              </p>
              <p>Total amount: ₹{totalAmt}</p>
              {becomeMentor && <p>You have also chosen to become a mentor.</p>}
            </div>
            <button className="ksm__closeButton" onClick={onClose}>
              Close
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  // Helper function to determine the step status
  const getStepStatus = (stepNumber) => {
    if (stepNumber < currentStep) return "completed";
    if (stepNumber === currentStep) return "active";
    return "inactive";
  };

  const boxShadowStyle = {
    boxShadow: `
      0px 0px 8px 0px #50505080,
      0px 4px 6px 0px #75757580,
      0px 8px 12px 0px #E8E8E880,
      0px 0px 2px 0px #FDC51380,
      inset 0px 2px 4px 0px #00000065,
      inset 0px 0px 2px 0px #00000080,
      0px 1px 0px 0px #E1E1E180,
      0px -1px 0px 0px #FFFFFF80
    `,
  };

  // Prevent background scroll when modal is open (like KidProfileModal)
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      if (window.innerWidth <= 768) {
        document.body.classList.add("kpm__modal-page-open");
      }
    } else {
      document.body.style.overflow = "auto";
      document.body.classList.remove("kpm__modal-page-open");
    }
    return () => {
      document.body.style.overflow = "auto";
      document.body.classList.remove("kpm__modal-page-open");
    };
  }, [isOpen]);

  if (!isOpen) return null;
  if (loading)
    return (
      <div className="ksm__sponsor-modal-overlay">
        <div className="ksm__sponsor-modal-container">
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <div className="ksm__loader">Loading...</div>
          </div>
        </div>
      </div>
    );

  return (
    <div className="ksm__sponsor-modal-overlay">
      <div className="ksm__sponsor-modal-container">
        {/* Back button in the top left corner */}
        <div style={{ position: "absolute", top: 16, left: 16, zIndex: 100 }}>
          <button
            className="ksm__backToProfileBtn"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
            }}
            onClick={() => {
              if (typeof onBackToProfile === "function") {
                onBackToProfile(kidId);
              } else {
                onClose();
              }
            }}
            aria-label="Back to Profile"
          >
            {/* You can use an icon here, e.g. a left arrow */}
            <span style={{ fontSize: 24, color: "#293E4F" }}>&larr;</span>
          </button>
        </div>
        {/* Existing close button */}
        <div className="ksm__sponsor-modal-close-btn" onClick={onClose}>
          <VscChromeClose size={24} color="#fff" />
        </div>

        <div className="ksm__sponsorModalContainer">
          {alreadySponsored ? (
            <div
              className="ksm__sponsorModalSubContainer"
              style={{ padding: "48px 24px", textAlign: "center" }}
            >
              <h3 className="ksm__sponsorHeading">
                {kid.name} is Already Sponsored
              </h3>
              <p style={{ marginTop: 16, color: "#555" }}>
                This child already has an active sponsorship. Thank you for your
                interest in supporting Shaagird kids.
              </p>
              <button
                className="ksm__closeButton"
                style={{ marginTop: 24 }}
                onClick={onClose}
              >
                Close
              </button>
            </div>
          ) : (
          <>
          <div className="ksm__headerBackground">
            <div className="ksm__desktopBg"></div>
            <img
              src="/modalImages/mobileProfileBg.png"
              alt="profile-bg"
              className="ksm__mobileViewSponsorBg"
            />
          </div>
          <div
            className="ksm__sponsorModalSubContainer"
            style={{ display: "flex", alignItems: "center" }}
          >
            <h3 className="ksm__sponsorHeading">Sponsor {kid.name}</h3>
            <div className="ksm__sponsorModalContent">
              <div
                className="ksm__sponsorModalContentLeft"
                style={{ border: "0.1px solid gray" }}
              >
                <div className="ksm__leftContentHeader">
                  <div className="ksm__leftContentHeaderLeft">
                    <img
                      src={kid.avatar_url || NoImg}
                      className="ksm__kidImage"
                      alt="Kid"
                    />
                    <div className="ksm__kidInfo">
                      <p className="ksm__kidNameLeft">{kid.name}</p>
                      <p className="ksm__kidInfoText">
                        <AgeCalculator birthyear={kid.date_of_birth} /> /
                        {kid.gender}
                      </p>
                    </div>
                  </div>
                  <button
                    className="ksm__dropDownButton"
                    onClick={handleDropDown}
                  >
                    <img
                      src="/modalImages/dropDownVector.png"
                      className="ksm__dropDownImage"
                    />
                  </button>
                </div>
                <div
                  className={`ksm__leftContentBody ${
                    dropDown ? "ksm__visible-content" : "ksm__hidden-content"
                  }`}
                >
                  <p className="ksm__leftBioHeading">Bio</p>
                  <p className="ksm__leftBioText">{kid.bio}</p>
                </div>
                {/* <div className={`ksm__leftContentEducation ${dropDown ? 'ksm__visible-content' : 'ksm__hidden-content'}`}>
                  <div className="ksm__educationHeader">
                    <p className="ksm__leftEducationHeading">Education Completed</p>
                    <p className="ksm__leftEducationText"> <ClassCalculator birthyear={kid.date_of_birth}/>%</p>
                  </div>
                  <div className="ksm__educationBar" style={{ 
                    height: '10px',
                    width: '100%',
                    backgroundColor: '#E0E0E0',
                    borderRadius: '10px',
                    position: 'relative',
                    marginTop: '8px',
                  }}>
                    <div 
                      className="ksm__educationBarFill" 
                      style={{ 
                        height: '100%',
                        width: `${ClassCalculator({birthyear: kid.date_of_birth})}%`, 
                        backgroundColor: '#299d8e', 
                        borderRadius: '10px',
                        position: 'relative'
                      }}
                    >
                      <div style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: '#299d8e',
                        position: 'absolute',
                        top: '-5px',  
                        right: '-10px',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                      }}></div>
                    </div>
                  </div>
                </div> */}
                <div
                  className={`ksm__leftContentBottom ${
                    dropDown ? "ksm__visible-content" : "ksm__hidden-content"
                  }`}
                >
                  <p className="ksm__leftEndComp">{kid.category_name}</p>
                </div>
              </div>
              <div className="ksm__sponsorModalContentRight">
                {renderStepContent()}
              </div>
            </div>
          </div>
          <div className="ksm__mobileViewModalBottom">
            {currentStep == 1 && (
              <button
                className="ksm__mobileViewNextButton"
                onClick={handleNext}
                disabled={months === 0}
              >
                Next
              </button>
            )}
            {currentStep == 2 &&
              (isSubscription ? (
                <button
                  className="ksm__mobileViewpaymentButton"
                  onClick={handleSubscriptionPayment}
                  disabled={subscriptionLoading}
                  style={{
                    backgroundColor: subscriptionLoading ? "#ccc" : "#FDC513",
                    color: "#000",
                    fontWeight: "bold",
                    cursor: subscriptionLoading ? "not-allowed" : "pointer",
                  }}
                >
                  {subscriptionLoading
                    ? "Creating Subscription..."
                    : `Subscribe & Pay ₹${kid.total_expenses?.toLocaleString()}/month`}
                </button>
              ) : (
                <button
                  className="ksm__mobileViewpaymentButton"
                  onClick={handlePayment}
                >
                  Proceed to Pay ₹{totalAmt?.toLocaleString()}
                </button>
              ))}
          </div>
          <div className="ksm__modalBottom">
            {[1, 2, 3].map((step, index) => (
              <React.Fragment key={step}>
                <div className="ksm__stepItem">
                  <div className={`ksm__stepNumber ${getStepStatus(step)}`}>
                    {getStepStatus(step) === "completed" ? (
                      <span className="ksm__tickIcon">✓</span>
                    ) : (
                      step
                    )}
                  </div>
                  <p className={`ksm__stepLabel ${getStepStatus(step)}`}>
                    {step === 1 && "Select Duration"}
                    {step === 2 && "Payment"}
                    {step === 3 && "Confirmation"}
                  </p>
                </div>

                {index < 2 && (
                  <div
                    className={`ksm__horizontalLine ${
                      getStepStatus(step + 1) === "completed" ||
                      getStepStatus(step) === "completed"
                        ? "completed"
                        : getStepStatus(step) === "active"
                          ? "active"
                          : "inactive"
                    }`}
                  ></div>
                )}
              </React.Fragment>
            ))}
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default KidSponsorModal;
